import { useState } from 'react';
import { addHours, format, parseISO } from 'date-fns';
import { AlertTriangle, Check, Copy } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface PartnerSecretRotationProps {
  signingSecretPlaintext: string;
  /** ISO timestamp when secret was rotated. If provided, modal also shows the 24h grace window. */
  rotatedAt?: string | null;
  /** Called when user clicks "I have saved the secret". The modal CANNOT close any other way. */
  onAcknowledge: () => void;
}

const COPY_FEEDBACK_MS = 2000;

export function PartnerSecretRotation({
  signingSecretPlaintext,
  rotatedAt,
  onAcknowledge,
}: PartnerSecretRotationProps): JSX.Element {
  const [copied, setCopied] = useState<boolean>(false);

  const handleCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(signingSecretPlaintext);
      setCopied(true);
      window.setTimeout(() => {
        setCopied(false);
      }, COPY_FEEDBACK_MS);
    } catch {
      // Clipboard API can fail (insecure context, permissions). Caller can
      // still manually select the text from the <code> block.
      setCopied(false);
    }
  };

  const graceWindowEndsAt: string | null = rotatedAt
    ? format(addHours(parseISO(rotatedAt), 24), "MMM d, yyyy 'at' h:mm a")
    : null;

  return (
    <Dialog open onOpenChange={() => undefined}>
      <DialogContent
        className="max-w-2xl [&>button]:hidden"
        onEscapeKeyDown={(event) => event.preventDefault()}
        onPointerDownOutside={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            Save your signing secret now
          </DialogTitle>
          <DialogDescription>
            This signing secret authenticates SSO tokens between your
            enterprise customer and BuildIt.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div
            role="alert"
            className="rounded-md border border-destructive/50 bg-destructive/10 p-4 text-sm font-medium text-destructive"
          >
            This is the only time you will see this secret. Copy it now and
            store it securely on your enterprise customer&apos;s server. After
            this dialog closes, the secret is unrecoverable.
          </div>

          <div className="space-y-2">
            <label
              htmlFor="partner-signing-secret"
              className="text-sm font-medium"
            >
              Signing secret
            </label>
            <div className="flex items-stretch gap-2">
              <code
                id="partner-signing-secret"
                className="flex-1 select-all break-all rounded-md bg-muted px-3 py-2 font-mono text-sm"
              >
                {signingSecretPlaintext}
              </code>
              <Button
                type="button"
                variant="outline"
                onClick={handleCopy}
                className="shrink-0"
                aria-label="Copy signing secret to clipboard"
              >
                {copied ? (
                  <>
                    <Check className="mr-2 h-4 w-4" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="mr-2 h-4 w-4" />
                    Copy
                  </>
                )}
              </Button>
            </div>
          </div>

          {graceWindowEndsAt ? (
            <p className="text-sm text-muted-foreground">
              <span className="font-medium text-foreground">
                Grace window:
              </span>{' '}
              the previous signing secret will continue to work until{' '}
              <span className="font-medium text-foreground">
                {graceWindowEndsAt}
              </span>
              .
            </p>
          ) : null}
        </div>

        <DialogFooter>
          <Button
            type="button"
            size="lg"
            onClick={onAcknowledge}
            className="w-full sm:w-auto"
          >
            I have saved the secret — close this dialog
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
